/* eslint-disable no-param-reassign */
/*
 * @Descripttion:
 * @version:
 * @Author: lxh3@meitu.com
 * @Date: 2025-05-24 16:00:20
 * @LastEditors: zwh2 zwh2@meitu.com
 * @LastEditTime: 2025-10-17 16:05:35
 */
import { getElementOptions, ElementName, IText } from '@meitu/whee-infinite-canvas';
import {
  StateCreator, UseBoundStore, StoreApi, create,
} from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import { FontFamilyItem } from '$/api/server.types';
// eslint-disable-next-line import/no-cycle
import useCanvasStore from '$/store/useCanvasStore';
// eslint-disable-next-line import/no-cycle
import { saveLayerData } from '$/pages/RoboneoCanvas/components/InfiniteCanvas/layerData';
import { submitModifyHistory } from '$/utils/canvasHistory';
import { ColorState, formatColor } from '$/utils/colorUtil';
import useCanvasObjectStore from './useCanvasObjectStore';
import type { Write, WithImmer } from './immer.d';
import type { StorePersist } from './persist.d';

interface ActiveTextStatus {
  fontFamily: string;
  fontSize: string;
  fontStyle: boolean;
  fontWeight: boolean;
  underline: boolean;
  color: ColorState;
  textAlign: string;
  charSpacing: string;
  lineHeight: string;
}

interface LastColorData {
  [uid: string]: ColorState[];
}
interface TextEditState {
  lastColorData: LastColorData;
  fontFamilyList: FontFamilyItem[];
  defaultFontFamily: FontFamilyItem;
  activeTextStatus: ActiveTextStatus | null;
  isEditingText: boolean;
}
const textEditSlice: StateCreator<TextEditState, [
  ['zustand/immer', never],
]> = () => ({
  lastColorData: {} as LastColorData,
  fontFamilyList: [],
  defaultFontFamily: {} as FontFamilyItem,
  activeTextStatus: null,
  isEditingText: false,
});
const useTextEditSote: UseBoundStore<Write<WithImmer<StoreApi<TextEditState>>, StorePersist<TextEditState, {
  lastColorData: LastColorData;
}>>> = create<TextEditState>()(immer(persist(textEditSlice, {
  name: 'useTextEditSote',
  version: 1,
  partialize: (state) => ({ lastColorData: state.lastColorData }),
})));
export default useTextEditSote;

/**
 * 设置最近颜色
 *
 * @export
 * @param {ColorState} color
 * @return {*}
 */
export function setLastColorData(color: ColorState) {
  // eslint-disable-next-line ban/ban
  const uid = localStorage.getItem('uid');
  if (!uid) return;
  useTextEditSote.setState((state) => {
    const colorList = state.lastColorData![uid];
    if (colorList) {
      const has = colorList.some((item) => {
        return JSON.stringify(item.rgb) === JSON.stringify(color.rgb);
      });
      // 如果已有相同颜色，则不缓存
      if (has) return;
      if (colorList.length >= 8) {
        const newList = [...state.lastColorData![uid]];
        newList.unshift(color);
        newList.pop();
        state.lastColorData![uid] = newList;
      } else {
        state.lastColorData![uid].unshift(color);
      }
    } else {
      state.lastColorData![uid] = [color];
    }
  });
}

/**
 * 设置字体list
 *
 * @export
 * @param {FontFamilyItem[]} list
 */
export function setFontFamilyList(list: FontFamilyItem[], defaultFontFamily: FontFamilyItem) {
  useTextEditSote.setState(() => ({
    fontFamilyList: list,
    defaultFontFamily,
  }));
}

/**
 * 设置文字是否处于编辑状态
 *
 * @export
 * @param {boolean} value
 */
export function setIsEditingText(value: boolean) {
  useTextEditSote.setState(() => ({ isEditingText: value }));
}

/**
 * 设置文字所有字段的状态
 *
 * @export
 * @param {(ActiveTextStatus | null)} status
 */
export function setActiveTextStatus(status: ActiveTextStatus | null) {
  useTextEditSote.setState(() => ({
    activeTextStatus: status,
  }));
}

/**
 * 更新文字所有字段的状态
 *
 * @export
 * @param {keyof ActiveTextStatus} key
 * @param {*} value
 */
export function updateActiveTextStatus(key: keyof ActiveTextStatus, value: any) {
  useTextEditSote.setState((state) => {
    if (state.activeTextStatus) {
      state.activeTextStatus = {
        ...state.activeTextStatus,
        [key]: value,
      };
    }
  });
}

/**
 * 初始化文本字段状态
 *
 * @export
 * @return {*}
 */
export function initTextStatus(text: IText) {
  const { render } = useCanvasStore.getState();
  // @ts-ignore
  const { letterSpacing } = getElementOptions.call(render, text);
  const color = formatColor((text?.getCurrentCharColor() || '#ffffff') as string);
  // hex返回的是Hex8格式，但组件中应该显示hex格式。需要把后两位去掉
  color.hex = color.hex.slice(0, -2);
  const { defaultFontFamily } = useTextEditSote.getState() as TextEditState;
  // 默认字体
  if (text?.fontFamily === 'Times New Roman' && defaultFontFamily.default_name) {
    text.set({
      fontFamily: defaultFontFamily.default_name || defaultFontFamily.name,
    });
    text._font_url_ = defaultFontFamily.font_file;
  }
  return {
    fontFamily: text.fontFamily,
    fontSize: `${Math.round(text?.fontSize ?? 120)}`,
    fontStyle: text.fontStyle === 'italic',
    fontWeight: text.fontWeight === 'bold',
    underline: !!text?.underline,
    color,
    textAlign: text?.textAlign || 'left',
    charSpacing: `${letterSpacing / 10}`,
    lineHeight: `${text?.lineHeight || 0}`,
  };
}

/**
 * 设置粗体
 */
export function setBoldText() {
  const isText = useCanvasObjectStore.getState().currentObjects.length === 1 && useCanvasObjectStore.getState().currentObjects[0]._name_ === ElementName.TEXT;
  if (!isText) return;
  const currentObject = useCanvasObjectStore.getState().currentObjects[0] as IText;
  submitModifyHistory(() => {
    const { render } = useCanvasStore.getState();

    const { activeTextStatus } = useTextEditSote.getState() as TextEditState;
    updateActiveTextStatus('fontWeight', !activeTextStatus?.fontWeight);
    currentObject?.set({
      fontWeight: activeTextStatus?.fontWeight ? 'normal' : 'bold',
    });
    render?._FC.renderAll();
    saveLayerData(null, [currentObject]);
  });
}

/**
 * 斜体
 */
export function setItalicText() {
  const isText = useCanvasObjectStore.getState().currentObjects.length === 1 && useCanvasObjectStore.getState().currentObjects[0]._name_ === ElementName.TEXT;
  if (!isText) return;
  const currentObject = useCanvasObjectStore.getState().currentObjects[0] as IText;
  submitModifyHistory(() => {
    const { render } = useCanvasStore.getState();
    const { activeTextStatus } = useTextEditSote.getState() as TextEditState;
    updateActiveTextStatus('fontStyle', !activeTextStatus?.fontStyle);
    currentObject?.set({
      fontStyle: activeTextStatus?.fontStyle ? 'normal' : 'italic',
    });
    render?._FC.renderAll();
    saveLayerData(null, [currentObject]);
  });
}

/**
 * 下划线
 */
export function setUnderlineText() {
  const isText = useCanvasObjectStore.getState().currentObjects.length === 1 && useCanvasObjectStore.getState().currentObjects[0]._name_ === ElementName.TEXT;
  if (!isText) return;
  const currentObject = useCanvasObjectStore.getState().currentObjects[0] as IText;
  submitModifyHistory(() => {
    const { render } = useCanvasStore.getState();
    const { activeTextStatus } = useTextEditSote.getState() as TextEditState;
    updateActiveTextStatus('underline', !activeTextStatus?.underline);
    currentObject?.set({ underline: !activeTextStatus?.underline });
    render?._FC.renderAll();
    saveLayerData(null, [currentObject]);
  });
}

/**
 * 文字对齐方式
 */
export function changeTextAlign(type: string) {
  const isText = useCanvasObjectStore.getState().currentObjects.length === 1 && useCanvasObjectStore.getState().currentObjects[0]._name_ === ElementName.TEXT;
  if (!isText) return;
  const currentObject = useCanvasObjectStore.getState().currentObjects[0] as IText;
  submitModifyHistory(() => {
    const { render } = useCanvasStore.getState();
    updateActiveTextStatus('textAlign', type);
    currentObject?.set({ textAlign: type });
    render?._FC.renderAll();
    saveLayerData(null, [currentObject]);
  });
}
